import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  CheckCircle, Truck, Shield, ClipboardList, AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReadOnlyField = ({ label, value, mono = false }) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</label>
    <p className={`text-sm text-slate-900 ${mono ? 'font-mono' : ''}`} style={{ fontFamily: mono ? 'monospace' : 'IBM Plex Sans, sans-serif' }}>
      {value || <span className="text-slate-400 italic">Not provided</span>}
    </p>
  </div>
);

export const OwnerOperatorReviewSection = ({ profile }) => {
  const t = profile.truck || {};
  const tr = profile.trailer || {};
  const p = profile.physical_damage_policy || {};
  const hasTruck = Object.values(t).some((v) => v !== null && v !== undefined && v !== '');
  const hasPolicy = Object.values(p).some((v) => v !== null && v !== undefined && v !== '');
  return (
    <div data-testid="owner-operator-review">
      <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-4 pt-6 border-t border-slate-200 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
        <Truck className="w-5 h-5 text-[#002FA7]" strokeWidth={2} />
        Equipment — Truck
      </h2>
      {hasTruck ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <ReadOnlyField label="Year" value={t.year} />
          <ReadOnlyField label="Make" value={t.make} />
          <ReadOnlyField label="Model" value={t.model} />
          <ReadOnlyField label="Fuel Type" value={t.fuel_type ? t.fuel_type[0].toUpperCase() + t.fuel_type.slice(1) : null} />
          <ReadOnlyField label="VIN" value={t.vin} mono />
          <ReadOnlyField label="Plate" value={t.license_plate ? `${t.license_plate} ${t.license_plate_state || ''}`.trim() : null} />
          <ReadOnlyField label="GVWR (lbs)" value={t.gvwr_lbs ? Number(t.gvwr_lbs).toLocaleString() : null} />
          <ReadOnlyField label="IRP #" value={t.irp_account} />
          <ReadOnlyField label="IFTA #" value={t.ifta_account} />
        </div>
      ) : (
        <p className="text-sm text-slate-400 italic mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Not provided</p>
      )}

      <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-4 pt-6 border-t border-slate-200 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
        <Truck className="w-5 h-5 text-[#002FA7]" strokeWidth={2} />
        Equipment — Trailer
      </h2>
      {profile.has_trailer && tr && Object.keys(tr).length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <ReadOnlyField label="Year" value={tr.year} />
          <ReadOnlyField label="Make" value={tr.make} />
          <ReadOnlyField label="Type" value={tr.trailer_type ? tr.trailer_type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : null} />
          <ReadOnlyField label="VIN" value={tr.vin} mono />
          <ReadOnlyField label="Plate" value={tr.license_plate ? `${tr.license_plate} ${tr.license_plate_state || ''}`.trim() : null} />
        </div>
      ) : (
        <p className="text-sm text-slate-400 italic mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No trailer.</p>
      )}

      <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-4 pt-6 border-t border-slate-200 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
        <Shield className="w-5 h-5 text-[#002FA7]" strokeWidth={2} />
        Physical Damage Insurance
      </h2>
      {hasPolicy ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <ReadOnlyField label="Carrier" value={p.carrier_name} />
          <ReadOnlyField label="Policy #" value={p.policy_number} mono />
          <ReadOnlyField label="Expiry" value={p.expiry_date} />
          <ReadOnlyField label="Coverage" value={p.coverage_amount_usd ? `$${Number(p.coverage_amount_usd).toLocaleString()}` : null} />
        </div>
      ) : (
        <p className="text-sm text-slate-400 italic mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Not provided — driver may still upload later.</p>
      )}
    </div>
  );
};

const _parseYm = (ym) => {
  if (!ym) return null;
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1);
};

const _fmtYm = (ym) => {
  const d = _parseYm(ym);
  if (!d) return null;
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

export const ApproveWithComplianceBlock = ({ driverId, onApprove }) => {
  const [check, setCheck] = useState(null);
  useEffect(() => {
    axios.get(`${API}/drivers/${driverId}/compliance-check`, { withCredentials: true })
      .then(r => setCheck(r.data))
      .catch(() => setCheck({ ok: false, issues: ['Compliance check failed to load'] }));
  }, [driverId]);
  const blocked = check && !check.ok;
  return (
    <div className="flex flex-col items-end gap-2" data-testid="approve-with-compliance">
      <button
        data-testid="approve-driver-detail-button"
        onClick={onApprove}
        disabled={blocked}
        title={blocked ? 'Resolve the compliance issues below before approving' : undefined}
        className={`text-white px-4 py-2 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${
          blocked ? 'bg-slate-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
        }`}
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      >
        <CheckCircle className="w-4 h-4" />
        Approve
      </button>
      {blocked && (
        <div className="max-w-sm bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-900 space-y-1" data-testid="compliance-issues-panel">
          <p className="font-semibold">Compliance requirements not met:</p>
          <ul className="list-disc list-inside">
            {check.issues.map((i, idx) => <li key={idx}>{i}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

export const CompanyDriverReviewSection = ({ profile }) => {
  const entries = Array.isArray(profile.past_employment) ? profile.past_employment : [];
  const sorted = [...entries].sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''));

  const gaps = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const laterStart = _parseYm(sorted[i].start_date);
    const earlierEnd = _parseYm(sorted[i + 1].end_date);
    if (laterStart && earlierEnd) {
      const gapDays = Math.round((laterStart - earlierEnd) / (1000 * 60 * 60 * 24));
      if (gapDays > 30) gaps.push({ from: sorted[i + 1].company_name, to: sorted[i].company_name, days: gapDays });
    }
  }

  return (
    <div data-testid="company-driver-review">
      <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-4 pt-6 border-t border-slate-200 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
        <ClipboardList className="w-5 h-5 text-[#002FA7]" strokeWidth={2} />
        Employment History (Past 3 Years)
      </h2>

      {gaps.length > 0 && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2" data-testid="employment-gap-warning">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <strong>{gaps.length} employment gap{gaps.length === 1 ? '' : 's'} over 30 days detected.</strong> FMCSA §391 requires accounting for every gap in the past 3 years. Follow up with the applicant.
          </p>
        </div>
      )}

      {sorted.length === 0 ? (
        <p className="text-sm text-slate-400 italic" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Not provided</p>
      ) : (
        <div className="space-y-3">
          {sorted.map((e, idx) => (
            <div key={idx} data-testid={`employment-review-entry-${idx}`} className="border border-slate-200 rounded-md p-4 bg-slate-50">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{e.company_name}</p>
                  <p className="text-xs text-slate-600 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {e.position || 'Position not specified'}
                    {' · '}
                    {_fmtYm(e.start_date) || '?'} to {e.end_date ? _fmtYm(e.end_date) : 'Present'}
                  </p>
                </div>
                {e.dot_regulated && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700 shrink-0">
                    DOT-REGULATED
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <ReadOnlyField label="Supervisor" value={e.supervisor_name} />
                <ReadOnlyField label="Supervisor Phone" value={e.supervisor_phone} />
                <ReadOnlyField label="Company Address" value={e.company_address} />
                <ReadOnlyField label="Reason for Leaving" value={e.reason_for_leaving} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
